<template>
  <div class="page-shell">
    <section class="page-panel" v-loading="loading">
      <div class="page-header">
        <div>
          <p class="eyebrow">客户工作台</p>
            <h1>{{ dashboardTitle }}</h1>
            <p class="page-subtitle">{{ dashboardSubtitle }}</p>
        </div>
        <div class="header-actions">
          <el-tag type="warning" effect="plain" round>待审批 {{ stats.pending }}</el-tag>
          <el-button :loading="loading" @click="loadProjects">刷新项目</el-button>
        </div>
      </div>

      <div class="stats-grid">
        <div class="stat-card">
          <span class="stat-label">项目总数</span>
          <strong>{{ stats.total }}</strong>
        </div>
        <div class="stat-card stat-card--warning">
          <span class="stat-label">待审批</span>
          <strong>{{ stats.pending }}</strong>
        </div>
        <div class="stat-card stat-card--primary">
          <span class="stat-label">执行中</span>
          <strong>{{ stats.active }}</strong>
        </div>
        <div class="stat-card stat-card--success">
          <span class="stat-label">已完成</span>
          <strong>{{ stats.completed }}</strong>
        </div>
      </div>

      <div class="section-header">
        <h2>项目列表</h2>
        <el-input v-model="searchQuery" placeholder="搜索项目名称" clearable class="search-box" />
      </div>

      <div class="project-list" v-if="filteredProjects.length">
        <article v-for="item in filteredProjects" :key="item.id" class="project-card">
          <div class="card-top">
            <div>
              <h3>{{ item.title }}</h3>
              <p>{{ item.summary }}</p>
            </div>
            <el-tag :type="projectStatusType(item)">{{ projectStatusLabel(item) }}</el-tag>
          </div>

          <div class="meta-row">
            <span>当前阶段：{{ item.currentPhaseLabel }}</span>
            <span>类型：{{ item.genre || '未设置' }}</span>
            <span>创建时间：{{ formatDate(item.created_at) }}</span>
            <span>进度：{{ item.progress }}%</span>
          </div>

          <el-progress :percentage="item.progress" :show-text="false" :stroke-width="10" />

          <div class="pending-box" v-if="item.pendingApprovalStage">
            <strong>当前待处理：</strong>
            <span>{{ approvalStageLabel(item.pendingApprovalStage) }}</span>
          </div>

          <div class="card-actions">
            <el-button v-if="item.workflowState === 'draft'" :loading="actionProjectId === item.id" type="primary" @click="handleStart(item)">
              启动工作流
            </el-button>
            <el-button v-if="item.pendingApprovalStage" :loading="actionProjectId === item.id" type="warning" @click="router.push(buildGenerationRoute(item.id, { stage: item.pendingApprovalStage as any }))">
              立即处理
            </el-button>
            <el-button
              v-else-if="['completed', 'delivered'].includes(item.workflowState)"
              @click="router.push(buildGenerationRoute(item.id))"
            >
              查看结果
            </el-button>
            <el-button v-else @click="router.push(buildGenerationRoute(item.id))">进入工作台</el-button>
            <el-button :loading="actionProjectId === item.id" type="danger" plain @click="handleDelete(item)">删除项目</el-button>
          </div>
        </article>
      </div>

      <el-empty v-else description="暂无项目，先创建一个新项目" />
    </section>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, reactive, ref } from 'vue'
import { useRouter } from 'vue-router'
import { ElMessage, ElMessageBox } from 'element-plus'

import { projectApi, type Project } from '@/api'
import { isAuthRedirectError } from '@/api/request'
import { getWorkflow } from '@/api/workflow'
import type { WorkflowInstance } from '@/types/workflow'
import { useProjectStore } from '@/stores/project'
import { useUserStore } from '@/stores/user'
import { getCurrentCustomerPhase, getLatestReviewableStage, getOverallWorkflowProgress } from '@/utils/workflowPresentation'
import { buildGenerationRoute, queueGenerationAction } from '@/utils/workflowGeneration'

type DashboardItem = Project & {
  workflowState: string
  progress: number
  pendingApprovalStage: string | null
  currentPhaseLabel: string
  summary: string
}

const router = useRouter()
const projectStore = useProjectStore()
const userStore = useUserStore()

const loading = ref(false)
const actionProjectId = ref<string | number | null>(null)
const searchQuery = ref('')
const workflowMap = ref<Record<string, WorkflowInstance>>({})
const dashboardTitle = computed(() => {
  if (userStore.isAdmin) {
    return '全站项目工作台'
  }
  return `${userStore.userInfo?.username || '用户'} 的项目工作台`
})

const dashboardSubtitle = computed(() => {
  if (userStore.isAdmin) {
    return '管理员默认查看全部用户项目，优先处理待审批项目，再推进草稿项目进入自动分析与制作。'
  }
  return '优先处理待审批项目，再推进草稿项目进入自动分析与制作。'
})

const stats = reactive({
  total: 0,
  pending: 0,
  active: 0,
  completed: 0,
})

const WORKFLOW_LOAD_TIMEOUT_MS = 8000

const progressFromWorkflow = (workflow?: WorkflowInstance) => {
  if (!workflow) return 0
  return getOverallWorkflowProgress(workflow)
}

const projects = computed<DashboardItem[]>(() => {
  return projectStore.projects.map((project) => {
    const workflow = workflowMap.value[String(project.id)]
    const workflowState = workflow?.state || project.status || 'draft'
    const progress = workflow ? progressFromWorkflow(workflow) : project.progress || 0
    const summarySource = workflow?.data?.input_data?.raw_requirement || project.description || ''
    const currentPhaseLabel = getCurrentCustomerPhase(workflow)?.title || '尚未开始'

    return {
      ...project,
      workflowState,
      progress,
      pendingApprovalStage: workflow?.pending_approval?.stage || null,
      currentPhaseLabel,
      summary: summarySource.slice(0, 120) || '暂无需求摘要',
    }
  })
})

const filteredProjects = computed(() => {
  const keyword = searchQuery.value.trim().toLowerCase()
  const filtered = !keyword ? projects.value : projects.value.filter((item) => item.title.toLowerCase().includes(keyword))
  return [...filtered].sort((left, right) => {
    const leftPriority = left.pendingApprovalStage ? 0 : left.workflowState === 'draft' ? 2 : 1
    const rightPriority = right.pendingApprovalStage ? 0 : right.workflowState === 'draft' ? 2 : 1

    if (leftPriority !== rightPriority) {
      return leftPriority - rightPriority
    }

    return new Date(right.updated_at || right.created_at || 0).getTime() - new Date(left.updated_at || left.created_at || 0).getTime()
  })
})

const refreshStats = () => {
  stats.total = projects.value.length
  stats.pending = projects.value.filter((item) => Boolean(item.pendingApprovalStage)).length
  stats.active = projects.value.filter((item) => !item.pendingApprovalStage && ['confirmed', 'in_progress', 'revision_in_progress'].includes(item.workflowState)).length
  stats.completed = projects.value.filter((item) => ['completed', 'delivered'].includes(item.workflowState)).length
}

const extractImportedScript = (description?: string | null) => {
  const text = String(description || '')
  const matched = text.match(/导入剧本：([\s\S]*)$/)
  return (matched?.[1] || text).trim()
}

const loadWorkflowSnapshot = async (projectId: number) => {
  let timeoutId: number | null = null

  try {
    const timeoutPromise = new Promise<WorkflowInstance | null>((resolve) => {
      timeoutId = window.setTimeout(() => resolve(null), WORKFLOW_LOAD_TIMEOUT_MS)
    })

    return await Promise.race([
      getWorkflow(projectId).catch(() => null),
      timeoutPromise,
    ])
  } finally {
    if (timeoutId !== null) {
      window.clearTimeout(timeoutId)
    }
  }
}

const loadProjects = async () => {
  loading.value = true
  try {
    const response = await projectApi.getList(userStore.isAdmin ? { all_projects: true, page_size: 100 } : undefined)
    const items = response.items || []
    projectStore.setProjects(items)
    workflowMap.value = {}
    refreshStats()

    const workflows = await Promise.all(
      items.map(async (project) => {
        const workflow = await loadWorkflowSnapshot(Number(project.id))
        return workflow ? [String(project.id), workflow] as const : null
      })
    )

    workflowMap.value = Object.fromEntries(workflows.filter(Boolean) as Array<readonly [string, WorkflowInstance]>)
    refreshStats()
  } catch (error) {
    if (isAuthRedirectError(error)) {
      return
    }
    ElMessage.error('加载项目列表失败')
  } finally {
    loading.value = false
  }
}

const handleStart = async (project: DashboardItem) => {
  actionProjectId.value = project.id
  try {
    const isScriptImport = String(project.genre || '').trim() === 'script_import'
    queueGenerationAction(project.id, {
      intent: 'start',
      stage: isScriptImport ? 'script' : 'proposal',
      startRequest: {
        project_id: Number(project.id),
        stage: isScriptImport ? 'script' : undefined,
        input_data: {
          raw_requirement: isScriptImport ? extractImportedScript(project.description) || project.title : project.description || project.title,
          project_type: project.genre,
          project_mode: isScriptImport ? 'script_import' : undefined,
          imported_script_content: isScriptImport ? extractImportedScript(project.description) || project.title : undefined,
          script_title: isScriptImport ? project.title : undefined,
          target_duration: project.target_duration,
        },
        require_proposal_approval: !isScriptImport,
      },
    })
    router.push(buildGenerationRoute(project.id, { intent: 'start', stage: isScriptImport ? 'script' : 'proposal' }))
  } catch (error) {
    ElMessage.error('启动工作流失败')
  } finally {
    actionProjectId.value = null
  }
}

const handleDelete = async (project: DashboardItem) => {
  try {
    await ElMessageBox.confirm(`确定删除项目“${project.title}”吗？删除后会从默认项目列表中移除。`, '删除项目', {
      confirmButtonText: '删除',
      cancelButtonText: '取消',
      type: 'warning',
    })

    actionProjectId.value = project.id
    await projectApi.delete(project.id)
    ElMessage.success('项目已删除')
    await loadProjects()
  } catch (error: any) {
    if (error !== 'cancel' && error !== 'close') {
      ElMessage.error(error?.response?.data?.detail || '删除项目失败')
    }
  } finally {
    actionProjectId.value = null
  }
}

const statusLabel = (status: string) => {
  const labels: Record<string, string> = {
    draft: '草稿',
    pending_confirm: '待方案确认',
    confirmed: '已确认',
    in_progress: '执行中',
    waiting_script_approval: '待剧本审批',
    waiting_storyboard_approval: '待分镜审批',
    waiting_final_review: '待终审',
    waiting_client_acceptance: '待客户验收',
    revision_requested: '待修改',
    revision_in_progress: '修改中',
    completed: '已完成',
    delivered: '已交付',
    failed: '失败',
  }
  return labels[status] || status
}

const statusType = (status: string) => {
  if (status.includes('waiting') || status === 'pending_confirm') return 'warning'
  if (status === 'completed' || status === 'delivered') return 'success'
  if (status === 'failed') return 'danger'
  if (status === 'in_progress' || status === 'confirmed') return 'primary'
  return 'info'
}

const projectStatusLabel = (project: DashboardItem) => {
  if (project.pendingApprovalStage) {
    return `待${approvalStageLabel(project.pendingApprovalStage)}`
  }
  return statusLabel(project.workflowState)
}

const projectStatusType = (project: DashboardItem) => {
  if (project.pendingApprovalStage) {
    return 'warning'
  }
  return statusType(project.workflowState)
}

const approvalStageLabel = (stage: string) => {
  const labels: Record<string, string> = {
    proposal: '方案确认',
    script: '剧本审批',
    storyboard: '分镜审批',
    final: '成品验收',
  }
  return labels[stage] || stage
}

const formatDate = (value?: string | null) => {
  if (!value) return '暂无'
  return new Date(value).toLocaleDateString('zh-CN')
}

onMounted(loadProjects)
</script>

<style scoped>
.page-shell {
  min-height: 100vh;
  padding: 32px;
  background: #f5f7fb;
}

.page-panel {
  max-width: 1280px;
  margin: 0 auto;
  padding: 32px;
  background: #fff;
  border-radius: 24px;
  box-shadow: 0 20px 60px rgba(15, 23, 42, 0.08);
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 24px;
  margin-bottom: 28px;
}

.eyebrow {
  margin: 0 0 8px;
  color: #4f46e5;
  font-size: 13px;
  font-weight: 600;
}

.page-header h1 {
  margin: 0;
  font-size: 32px;
}

.page-subtitle {
  margin: 12px 0 0;
  color: #64748b;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 16px;
  margin-bottom: 28px;
}

.stat-card {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 20px;
  border-radius: 18px;
  background: #f8fafc;
}

.stat-card strong {
  font-size: 32px;
  color: #0f172a;
}

.stat-card--warning {
  background: #fff7ed;
}

.stat-card--primary {
  background: #eef2ff;
}

.stat-card--success {
  background: #ecfdf5;
}

.stat-label {
  color: #64748b;
  font-size: 13px;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.section-header h2 {
  margin: 0;
}

.search-box {
  width: 280px;
}

.project-list {
  display: grid;
  gap: 16px;
}

.project-card {
  padding: 24px;
  border: 1px solid #e2e8f0;
  border-radius: 20px;
}

.card-top {
  display: flex;
  justify-content: space-between;
  gap: 20px;
  margin-bottom: 16px;
}

.card-top h3 {
  margin: 0 0 8px;
  font-size: 20px;
}

.card-top p {
  margin: 0;
  color: #64748b;
  line-height: 1.7;
}

.meta-row {
  display: flex;
  flex-wrap: wrap;
  gap: 18px;
  margin-bottom: 14px;
  color: #64748b;
  font-size: 13px;
}

.pending-box {
  margin-top: 14px;
  padding: 12px 14px;
  border-radius: 14px;
  background: #fff7ed;
  color: #9a3412;
}

.card-actions {
  display: flex;
  gap: 12px;
  margin-top: 18px;
}

@media (max-width: 960px) {
  .stats-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 768px) {
  .page-shell {
    padding: 16px;
  }

  .page-panel {
    padding: 24px;
  }

  .page-header,
  .section-header,
  .card-top,
  .card-actions {
    display: grid;
    grid-template-columns: 1fr;
  }

  .header-actions {
    display: grid;
    grid-template-columns: 1fr;
  }

  .stats-grid {
    grid-template-columns: 1fr;
  }

  .search-box {
    width: 100%;
  }
}
</style>